import { TemplateConfig, ReportDocType } from './types';

export const REPORTS_DOC_TYPES: ReportDocType[] = ['ONE_PAGER', 'ENGAGEMENT_LETTER', 'NDA', 'BUREAU_KYC_CONSENT', 'APPLICATION_FORM'];

export const AICA_REPORT_ID = 'AICA_REPORT' as const;

/**
 * The one template card that is NOT a polus template: the AICA Report opens the
 * AicaReportV2 module directly and never goes through the compose flow, so it has no
 * ViewConfig doc and always leads the grid.
 */
export const AICA_REPORT_CARD: TemplateConfig = {
    id: AICA_REPORT_ID,
    label: 'AICA Report',
    description: 'AI-generated investment report with company overview, financials & key metrics.',
    topStripeColor: '#6021b3',
    isAicaReport: true,
};

/**
 * Degraded-mode grid, used ONLY when the per-PNO catalog fetch fails — an outage keeps
 * the previously-shipped cards rather than emptying the Workspace.
 *
 * This is NOT the copy source. Labels, descriptions and order for a live grid come from
 * the template ViewConfigs (godzilla) via polus GET /config/templates, which is what lets
 * a new template ship without a release here. Do not add a new template to this list —
 * add a ViewConfig doc. These entries exist because these four templates predate the
 * catalog endpoint and an offline grid should still be usable.
 *
 * requiredFormDataVars MUST mirror the template bodies here. A generate that fires before
 * the catalog resolves reads its requirements from this list, and a missing entry means
 * compose omits the variable and the document renders the missing-data sentinel.
 */
export const FALLBACK_TEMPLATE_CATALOG: TemplateConfig[] = [
    AICA_REPORT_CARD,
    {
        id: 'ONE_PAGER',
        label: 'Detailed One Pager',
        description: 'One-page deal memo: entity snapshot, business overview, management, financials & debt schedule.',
    },
    {
        id: 'ENGAGEMENT_LETTER',
        label: 'Engagement Letter',
        description: 'Formal engagement letter with terms, scope and conditions for the mandate.',
        requiredFormDataVars: ['companyAddress'],
    },
    {
        id: 'NDA',
        label: 'NDA',
        description: 'Non-disclosure agreement: entity snapshot, business overview, management, financials & debt.',
        requiredFormDataVars: ['companyAddress'],
    },
    {
        id: 'BUREAU_KYC_CONSENT',
        label: 'Bureau & KYC Consent',
        description: 'Bureau and KYC consent form authorising credit and identity checks.',
    },
];

export const REPORT_STATUS_LABELS: Record<string, string> = {
    READY: 'Ready',
    IN_PROGRESS: 'In Progress',
};

export const DOC_OPEN_SOURCE = 'REPORT_TOP_BAR' as const;

export const REPORTS_SECTION_TITLES = {
    START_NEW: 'Start a new report',
    REPORTS: 'Reports',
} as const;

export const TEMPLATE_GRID = {
    COLLAPSED_TEMPLATE_COUNT: 3,
} as const;

export const REPORTS_SEARCH_PLACEHOLDER = 'Search Report';

export const EXPLORE_TEMPLATES_LABEL = 'Explore all';
export const VIEW_LESS_TEMPLATES_LABEL = 'View Less';
export const NEW_SPREADSHEET_LABEL = 'New Spreadsheet';
export const NEW_DOCUMENT_LABEL = 'New Document';
export const GENERATE_NEW_LABEL = 'Generate New';
export const GENERATING_LABEL = 'Generating…';

export const IMPORT_ACCEPTED_EXTENSIONS = ['docx', 'doc', 'md', 'markdown', 'xlsx', 'xls', 'csv'] as const;
export const IMPORT_ACCEPT_ATTR = '.docx,.doc,.md,.markdown,.xlsx,.xls,.csv';

export const IMPORT_FORMAT = {
    HTML: 'html',
    MARKDOWN: 'markdown',
} as const;

export type ImportFormat = (typeof IMPORT_FORMAT)[keyof typeof IMPORT_FORMAT];

export const EXTENSION_TO_FORMAT: Record<string, ImportFormat> = {
    html: IMPORT_FORMAT.HTML,
    htm: IMPORT_FORMAT.HTML,
    md: IMPORT_FORMAT.MARKDOWN,
    markdown: IMPORT_FORMAT.MARKDOWN,
};

export const REPORT_EDITOR = {
    UPLOAD_SOURCE: 'report',
    PLACEHOLDER: '',
    SAVE_LABEL: 'Save',
    SAVING_LABEL: 'Saving…',
    BACK_LABEL: 'Back',
    SAVE_VERSION: 1,
} as const;

export const REPORT_TOASTS = {
    IMPORT_FAILED: 'Could not read that file. Please upload a valid HTML or Markdown report.',
    SAVE_SUCCESS: 'Report saved',
    SAVE_FAILED: 'Could not save the report. Please try again.',
    GENERATE_FAILED: 'Could not generate the report. Starting with a blank document.',
    LOAD_FAILED: 'Could not load the saved report. Starting with a blank document.',
} as const;

export const CUSTOM_DOC_TYPE = 'CUSTOM';

export const ROW_ACTION_LABELS = {
    EDIT_NAME: 'Edit name',
    ARCHIVE: 'Archive',
    MOVE_TO_ACTIVE: 'Move to active',
    MORE: 'More actions',
} as const;

export const ACTIVE_LABEL = 'Active';
export const ARCHIVED_LABEL = 'Archived';

export const ACTIVE_EMPTY_STATE_TEXT = 'No reports yet. Generate one from a template above.';
export const ARCHIVED_EMPTY_STATE_TEXT = 'No archived reports added yet';
export const SEARCH_EMPTY_STATE_TEXT = (searchTerm: string) => `No results for '${searchTerm}'`;

export const REPORT_LIFECYCLE_TOASTS = {
    DELETE_FAILED: 'Could not delete the report. Please try again.',
    ARCHIVE_FAILED: 'Could not archive the report. Please try again.',
    UNARCHIVE_FAILED: 'Could not unarchive the report. Please try again.',
} as const;

export const ARCHIVE_CONFIRM = {
    TITLE: 'Archive report?',
    MESSAGE: 'This report and all its versions will move to the Archived tab. You can move it back to Active anytime.',
    CONFIRM: 'Archive',
    CANCEL: 'Cancel',
} as const;

export const SAVE_DIALOG = {
    HEADING: 'Save report',
    NAME_PLACEHOLDER: 'Report name',
    TYPE_LABEL: 'Document type',
    TYPE_PLACEHOLDER: 'Select type',
    CUSTOM_TYPE_TEXT: 'Add custom type',
    CUSTOM_TYPE_PLACEHOLDER: 'Custom type name',
    CONFIRM_LABEL: 'Save',
    CANCEL_LABEL: 'Cancel',
} as const;
